import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Linkedin, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { labelize, slugify } from './ui';
import { usePublicData } from '../../../context/PublicDataContext';
import api from '../../../utils/api';

export default function PublicFooter() {
  const { settings, categories, menuPages, siteName } = usePublicData();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const year = new Date().getFullYear();
  const topCats = categories.slice(0, 6);
  const popular = categories.slice(0, 10);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post('/website/public/enquiry', {
        name: 'Newsletter subscriber',
        phone: '—',
        email,
        enquiry_type: 'Newsletter',
        message: `Newsletter signup: ${email}`,
      });
      toast.success('You’re on the list.');
      setEmail('');
    } catch {
      toast.error('Could not subscribe right now.');
    } finally {
      setBusy(false);
    }
  };

  const col = 'text-white text-[11px] uppercase tracking-[0.18em] mb-5 font-semibold';
  const lnk = 'block text-[13.5px] opacity-80 hover:opacity-100 hover:text-[#d98a52] transition-colors py-1';

  return (
    <footer className="bg-[#160f0a] text-[#cbbca4]">
      {/* newsletter band */}
      <div className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-14 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#d98a52] mb-3">The Journal</div>
            <h3 className="font-display text-2xl md:text-3xl text-white leading-tight">
              New collections, projects and workshop notes.
            </h3>
          </div>
          <form onSubmit={subscribe} className="flex items-center border-b border-white/25 focus-within:border-[#d98a52] transition-colors">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              className="flex-1 bg-transparent py-3 outline-none text-white placeholder:text-white/40 text-sm"
              aria-label="Email address"
            />
            <button disabled={busy} className="p-2 text-white hover:text-[#d98a52] transition-colors" aria-label="Subscribe">
              <ArrowRight />
            </button>
          </form>
        </div>
      </div>

      {/* link columns */}
      <div className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-2 md:grid-cols-5 gap-10">
        <div className="col-span-2 md:col-span-1">
          <div className="font-display text-white text-xl mb-4">{siteName.toUpperCase()}</div>
          <p className="text-[13.5px] leading-relaxed opacity-80">
            {settings.tagline || 'Furniture manufacturing for dealers, retailers and hospitality projects.'}
          </p>
          <div className="flex gap-3 mt-6">
            {settings.instagram_url && <Social href={settings.instagram_url} icon={Instagram} />}
            {settings.facebook_url && <Social href={settings.facebook_url} icon={Facebook} />}
            {settings.linkedin_url && <Social href={settings.linkedin_url} icon={Linkedin} />}
          </div>
        </div>

        <div>
          <div className={col}>Collection</div>
          {topCats.length ? (
            topCats.map((c) => (
              <Link key={c.name} to={`/products?category=${encodeURIComponent(slugify(c.name))}`} className={lnk}>
                {labelize(c.name)}
              </Link>
            ))
          ) : (
            <Link to="/products" className={lnk}>All products</Link>
          )}
        </div>

        <div>
          <div className={col}>Explore</div>
          <Link to="/craftsmanship" className={lnk}>Craftsmanship</Link>
          <Link to="/projects" className={lnk}>Projects</Link>
          <Link to="/makers" className={lnk}>Makers</Link>
          <Link to="/about" className={lnk}>About</Link>
        </div>

        <div>
          <div className={col}>Business</div>
          <Link to="/dealers" className={lnk}>Dealers</Link>
          <Link to="/hospitality" className={lnk}>Hospitality</Link>
          <Link to="/b2b" className={lnk}>Become a Partner</Link>
          {menuPages?.slice(0, 3).map((p) => (
            <Link key={p.id} to={`/${p.slug}`} className={lnk}>{p.title}</Link>
          ))}
        </div>

        <div>
          <div className={col}>Contact</div>
          <div className="text-[13.5px] opacity-80 space-y-2">
            {settings.address && <p>{settings.address}</p>}
            {settings.phone && <p>{settings.phone}</p>}
            {settings.email && <p>{settings.email}</p>}
            {settings.business_hours && <p className="opacity-70">{settings.business_hours}</p>}
          </div>
        </div>
      </div>

      {/* popular searches (SEO internal linking) */}
      {popular.length > 0 && (
        <div className="border-t border-white/10">
          <div className="max-w-6xl mx-auto px-6 py-8">
            <div className="text-[10.5px] uppercase tracking-[0.2em] text-white/50 mb-3">Popular searches</div>
            <div className="flex flex-wrap gap-x-4 gap-y-2 text-[12px] opacity-70">
              {popular.map((c, i) => (
                <React.Fragment key={c.name}>
                  <Link to={`/products?category=${encodeURIComponent(slugify(c.name))}`} className="hover:text-[#d98a52] hover:opacity-100">
                    {labelize(c.name)}
                  </Link>
                  {i < popular.length - 1 && <span className="opacity-30">/</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[12px]">
          <span>© {year} {siteName}. All rights reserved.</span>
          <div className="flex gap-5">
            <Link to="/privacy-policy" className="opacity-70 hover:opacity-100 hover:text-[#d98a52]">Privacy</Link>
            <Link to="/terms" className="opacity-70 hover:opacity-100 hover:text-[#d98a52]">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function Social({ href, icon: Icon }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="w-9 h-9 border border-white/20 flex items-center justify-center hover:border-[#d98a52] hover:text-[#d98a52] transition-colors">
      <Icon className="w-4 h-4" />
    </a>
  );
}
